import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { publicAxios } from "@/lib/axios";
import { AxiosError } from "axios";
import { rememberCampaignRecovery } from '@/lib/campaigns/campaignEntryRouting';
import { SUPER_HERO_ENTRY_PATH } from '@/lib/campaigns/superHeroCampaign';

const ForgotPassword = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const campaignReturn = searchParams.get('redirect') === SUPER_HERO_ENTRY_PATH ? SUPER_HERO_ENTRY_PATH : null;
  const loginDestination = campaignReturn || '/login';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    rememberCampaignRecovery(localStorage, campaignReturn, Date.now());

    try {
      await publicAxios.post('/api/auth/forgot-password', {
        email: email,
      });

      setIsEmailSent(true);
      toast({
        title: t('forgotPassword.toast.successTitle'),
        description: t('forgotPassword.toast.successDescription'),
      });
    } catch (error) {
      const axiosError = error as AxiosError;
      toast({
        title: t('forgotPassword.toast.errorTitle'),
        description: (axiosError.response?.data as any)?.error?.message || t('forgotPassword.toast.errorDescription'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isEmailSent) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-gray-800 border-gray-700">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <img
                src="/images/autoclipper-logo-full.png"
                alt="Autoclipper Logo"
                className="h-12 w-auto"
              />
            </div>
            <CardTitle className="text-2xl font-bold text-white">{t('forgotPassword.emailSent.title')}</CardTitle>
            <CardDescription className="text-gray-400">
              {t('forgotPassword.emailSent.subtitle')}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-gray-300 mb-4">
              {t('forgotPassword.emailSent.message')}
            </p>
            <Button
              onClick={() => {
                setIsEmailSent(false);
                setEmail("");
              }}
              className="w-full bg-gradient-to-r from-lime-400 to-cyan-400 hover:from-lime-500 hover:to-cyan-500 text-black font-semibold rounded-full shadow-lg"
            >
              {t('forgotPassword.emailSent.sendAgain')}
            </Button>
            <div className="mt-4">
              <Link
                to={loginDestination}
                className="text-green-400 hover:text-green-300 underline"
              >
                {t('forgotPassword.emailSent.backToLogin')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-800 border-gray-700">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img
              src="/images/autoclipper-logo-full.png"
              alt="Autoclipper Logo"
              className="h-12 w-auto"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-white">{t('forgotPassword.title')}</CardTitle>
          <CardDescription className="text-gray-400">
            {t('forgotPassword.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">
                {t('common.email')}
              </Label>
              <Input
                id="email"
                type="email"
                placeholder={t('forgotPassword.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-lime-400 to-cyan-400 hover:from-lime-500 hover:to-cyan-500 text-black font-semibold rounded-full shadow-lg"
              disabled={isLoading}
            >
              {isLoading ? t('forgotPassword.submitting') : t('forgotPassword.submitButton')}
            </Button>

            <div className="text-center text-sm text-gray-400">
              {t('forgotPassword.rememberedPassword')}{" "}
              <Link
                to={loginDestination}
                className="text-green-400 hover:text-green-300 underline"
              >
                {t('forgotPassword.loginLink')}
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
